<template>
  <div >
    <emd-collapse :title="$t('creditCard')" expanded>
      <template v-slot:icon>
        <label class="emd-checkbox">
          <input
            type="checkbox"
            class="emd-checkbox__input"
            v-model="creditCardEnabled"
          />
          <div class="emd-checkbox__area"></div>
        </label>
      </template>
      <template v-slot:subtitle>
        <span class="subtitle">{{ $t('receiveLabel') }}</span>
      </template>
      <template v-slot:content>
        <div class="eu-flex">
          <div class="eu-flex-item eu-basis_12 eu-basis_6_md">
            <div class="emd-form-group">
              <label class="emd-label">{{ $t('installment') }}</label>
              <div class="emd-select">
                <select :value="creditCardSettings.max_installments" @change="changeValue($event, 'max_installments')">
                  <option v-for="(item, index) in installments" :key="index" :value="item.value">{{ item.label }}</option>
                </select>
                <div class="emd-select__arrow"></div>
              </div>
            </div>
          </div>
          <div class="eu-flex-item eu-basis_12 eu-basis_6_md">
            <div class="emd-form-group">
              <label class="emd-label">{{ $t('invoiceText') }} <span class="label-optional">({{ $t('optional') }})</span></label>
              <input
                :value="creditCardSettings.statement_descriptor"
                @input="changeValue($event, 'statement_descriptor')"
                :class="{'_state_error': $v.creditCardSettings.statement_descriptor.$invalid && showFeedbacks.status}"
                class="emd-input"
                maxlength="13"
                :placeholder="$t('invoiceTextPlaceholder')"
              />
              <p class="emd-input-feedback _state_error" v-if="!$v.creditCardSettings.statement_descriptor.maxLength && showFeedbacks.status">
                {{ $t('hint.length', { length: '13' }) }}
              </p>
            </div>
          </div>
          <div class="eu-flex-item eu-basis_auto">
            <div class="emd-form-group">
              <label class="emd-label">{{ $t('feeInstallment') }}</label>
              <div class="emd-select">
                <select :value="creditCardSettings.rated_installments" @change="changeValue($event, 'rated_installments')">
                  <option v-for="(item, index) in feeInstallments" :key="index" :value="item.value">{{ item.label }}</option>
                </select>
                <div class="emd-select__arrow"></div>
              </div>
            </div>
          </div>
          <div class="eu-flex-item eu-basis_12 eu-basis_6_md" v-if="hasRatedInstallments">
            <div class="emd-form-group">
              <label class="emd-label">{{ $t('fee') }}<span class="label-optional"> ({{ $t('percentage') }})</span></label>
              <div class="emd-select">
                <div class="eu-flex eu-align-items_center">
                  <div class="eu-flex-item">
                    <emd-input-money
                      name="fee"
                      v-model="fee"
                      prefix="%"
                      numberFormat="decimal"
                      :class="{'emd-input': true, '_state_error': (!$v.fee.minValue || !$v.fee.maxValue) && showFeedbacks.status}"
                    />
                    <div v-if="showFeedbacks.status">
                      <p class="emd-input-feedback _state_error" v-if="!$v.fee.minValue">
                        {{ $t('hint.minValue', { value: '% 0,00' }) }}
                      </p>
                      <p class="emd-input-feedback _state_error" v-if="!$v.fee.maxValue">
                        {{ $t('hint.maxValue', { value: '% 1000,00' }) }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="eu-flex-item eu-basis_12 eu-basis_12_md">
            <emd-collapse :title="$t('advancedConfiguration')" variant="subcontainer">
              <template slot="content">
                <div class="emd-form-group">
                  <label class="emd-label">
                  {{ $t('operationType') }}
                  <emd-tippy
                    id="company"
                    target="_blank"
                    rel="noopener noreferrer"
                    tag="a"
                    class="_has-tooltip"
                    type="action-button"
                    position="top"
                    :content="$t('operationTypeTippy')"
                  >
                    <i class="uil uil-info-circle emd-text _color_success"/>
                  </emd-tippy>
                  </label>
                  <div class="emd-select">
                    <select :value="creditCardSettings.operation_type" @change="changeValue($event, 'operation_type')">
                      <option v-for="(item, index) in operationTypes" :key="index" :value="item.value">{{ item.label }}</option>
                    </select>
                    <div class="emd-select__arrow"></div>
                  </div>
                </div>
              </template>
            </emd-collapse>
          </div>
        </div>
      </template>
    </emd-collapse>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import EmdInputMoney from '@/components/project/EmdInputMoney'
import { EmdCollapse, EmdTippy } from 'emerald-vue/src/components'
import { maxLength } from 'vuelidate/lib/validators'

export default {
  name: 'CreditCard',
  components: {
    EmdCollapse,
    EmdTippy,
    EmdInputMoney
  },
  data () {
    return {
      money: {
        decimal: ',',
        thousands: '.',
        prefix: '% ',
        suffix: '',
        precision: 2,
        masked: false
      },
      fee: 0,
      installments: [],
      operationTypes: [
        {
          label: this.$t('authAndCapture'),
          value: 'auth_and_capture'
        },
        {
          label: this.$t('justAuth'),
          value: 'auth_only'
        }
      ]
    }
  },
  computed: {
    ...mapGetters(['creditCardSettings', 'paymentMethods', 'showFeedbacks']),
    hasRatedInstallments () {
      return this.creditCardSettings.rated_installments > 0
    },
    creditCardEnabled: {
      get () {
        return this.paymentMethods.credit_card_enabled
      },
      set (newValue) {
        this.CREDIT_CARD_PAYMENT(newValue)
      }
    },
    feeInstallments () {
      const feeInstallments = []

      feeInstallments.push({
        label: this.$t('noInstallment'),
        value: 0
      })

      for (let i = 2; i <= Number(this.creditCardSettings.max_installments); i++) {
        feeInstallments.push({
          label: `${this.$t('fromThe')} ${i}ª`,
          value: i - 1
        })
      }

      feeInstallments.push({
        label: this.$t('allInstallments'),
        value: this.creditCardSettings.max_installments
      })

      return feeInstallments
    }
  },
  validations () {
    if (this.hasRatedInstallments) {
      return {
        creditCardSettings: {
          statement_descriptor: {
            maxLength: maxLength(13)
          }
        },
        fee: {
          minValue: () => this.fee >= 0,
          maxValue: () => this.fee <= 1000
        }
      }
    }
    return {
      creditCardSettings: {
        statement_descriptor: {
          maxLength: maxLength(13)
        }
      }
    }
  },
  mounted () {
    this.fee = this.creditCardSettings.interest_rate
  },
  created () {
    this.createInstallments()
  },
  methods: {
    ...mapActions(['UPDATE_CREDIT_CARD_DATA', 'CREDIT_CARD_PAYMENT']),
    changeValue (event, attribute) {
      this.UPDATE_CREDIT_CARD_DATA({
        value: event.target?.value,
        attribute
      })
      this.$emit('formIsValid', this.$v.$invalid)
    },
    subtractQuantity () {
      if (this.creditCardSettings.interest_rate > 1) {
        this.creditCardSettings.interest_rate = Number(this.creditCardSettings.interest_rate) - 1
      }
    },
    addQuantity () {
      this.creditCardSettings.interest_rate = Number(this.creditCardSettings.interest_rate) + 1
    },
    createInstallments () {
      this.installments.push({
        label: this.$t('inCash'),
        value: 1
      })

      for (let i = 2; i <= 18; i++) {
        this.installments.push({
          label: `${this.$t('in')} ${i}x`,
          value: i
        })
      }
    }
  },
  watch: {
    fee: {
      handler () {
        this.UPDATE_CREDIT_CARD_DATA({
          value: this.fee,
          attribute: 'interest_rate'
        })
        this.$emit('formIsValid', this.$v.$invalid)
      },
      immediate: true
    }
  }
}
</script>

<i18n>
{
  "pt-BR": {
    "creditCard": "Cartão de crédito",
    "receiveLabel": "Receba no prazo negociado",
    "installment": "Parcelamento",
    "optional": "opcional",
    "invoiceText": "Texto na fatura",
    "feeInstallment": "Parcelas com acréscimo",
    "operationType": "Tipo da operação",
    "operationTypeTippy": "Selecionar ''Apenas autorização'' vai autorizar e reservar o limite no cartão, sem capturar a cobrança.",
    "fee": "Acréscimo no preço ao mês",
    "advancedConfiguration": "Configurações avançadas",
    "authAndCapture": "Autorização e captura",
    "justAuth": "Apenas autorização",
    "inCash": "Apenas à vista",
    "in": "Em até",
    "fromThe": "A partir da",
    "noInstallment": "Nenhuma parcela",
    "theFirst": "Apenas a primeira",
    "allInstallments": "Todas as parcelas",
    "invoiceTextPlaceholder": "É como vai aparecer na fatura",
    "hint": {
      "length": "Máximo de {length} caracteres",
      "minValue": "Valor minimo {value}",
      "maxValue": "Valor máximo {value}"
    },
    "percentage": "em %"
  },
  "en-US": {
    "creditCard": "Credit card",
    "receiveLabel": "Receive within the agreed period",
    "installment": "Installment",
    "optional": "optional",
    "invoiceText": "Text on invoice",
    "feeInstallment": "Installments with additions",
    "operationType": "Operation type",
    "operationTypeTippy": "Selecting ''Authorization Only'' will authorize and reserve the limit on the card, without capturing the charge.",
    "fee": "Addition per month",
    "advancedConfiguration": "Advanced Configuration",
    "authAndCapture": "Auth and capture",
    "justAuth": "Just auth",
    "inCash": "Just in cash",
    "in": "In",
    "fromThe": "From the",
    "noInstallment": "No installment",
    "theFirst": "Just the first",
    "allInstallments": "All installments",
    "invoiceTextPlaceholder": "This is how it will appear on the invoice",
    "hint": {
      "length": "This field cannot have more than {length} characters",
      "minValue": "Min value {value}",
      "maxValue": "Max value {value}"
    },
    "percentage": "in %"
  }
}
</i18n>

<style>
.config {
  font-weight: 700;
  color: #72737A;
}
</style>
